import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from '../Shared/API/config';

export const AccountsApi = createApi({
  reducerPath: 'AccountsApi',
  baseQuery,
  tagTypes: ['categories', 'expenses', 'incomes'],
  endpoints: builder => ({
    getCategories: builder.query<
      any,
      { search?: string; type?: string } | void
    >({
      query: params => {
        const queryParams = new URLSearchParams();
        if (params?.search) queryParams.append('q', params.search);
        if (params?.type) queryParams.append('type', params.type);

        return `/accounting/categories?${queryParams.toString()}`;
      },
      providesTags: ['categories'],
    }),
    createCategory: builder.mutation<any, any>({
      query: newCategory => ({
        url: '/accounting/categories',
        method: 'POST',
        body: newCategory,
      }),
      invalidatesTags: ['categories'],
    }),
    updateCategory: builder.mutation<any, { id: string; updates: any }>({
      query: ({ id, updates }) => ({
        url: `/accounting/categories/${id}`,
        method: 'PATCH',
        body: updates,
      }),
      invalidatesTags: ['categories'],
    }),
    deleteCategory: builder.mutation<void, string>({
      query: id => ({
        url: `/accounting/categories/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['categories'],
    }),

    getExpenses: builder.query<
      any,
      { search?: string; categoryId: string } | void
    >({
      query: params => {
        const queryParams = new URLSearchParams();
        if (params?.search) queryParams.append('q', params.search);
        if (params?.categoryId)
          queryParams.append('category', params.categoryId);

        return `/accounting/expenses?${queryParams.toString()}`;
      },
      providesTags: ['expenses'],
    }),
    createExpense: builder.mutation<any, any>({
      query: newExpense => ({
        url: '/accounting/expenses',
        method: 'POST',
        body: newExpense,
      }),
      invalidatesTags: ['expenses'],
    }),
    updateExpense: builder.mutation<any, { id: string; updates: any }>({
      query: ({ id, updates }) => ({
        url: `/accounting/expenses/${id}`,
        method: 'PATCH',
        body: updates,
      }),
      invalidatesTags: ['expenses'],
    }),
    deleteExpense: builder.mutation<void, string>({
      query: id => ({
        url: `/accounting/expenses/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['expenses'],
    }),
    approveExpense: builder.mutation<void, string>({
      query: id => ({
        url: `/accounting/expenses/${id}/approve`,
        method: 'POST',
      }),
      invalidatesTags: ['expenses'],
    }),
    rejectExpense: builder.mutation<void, string>({
      query: id => ({
        url: `/accounting/expenses/${id}/reject`,
        method: 'POST',
      }),
      invalidatesTags: ['expenses'],
    }),

    getIncomes: builder.query<
      any,
      { search?: string; categoryId?: string } | void
    >({
      query: params => {
        const queryParams = new URLSearchParams();
        if (params?.search) queryParams.append('q', params.search);
        if (params?.categoryId)
          queryParams.append('category', params.categoryId);

        return `/accounting/income?${queryParams.toString()}`;
      },
      providesTags: ['incomes'],
    }),
    createIncome: builder.mutation<any, any>({
      query: newIncome => ({
        url: '/accounting/income',
        method: 'POST',
        body: newIncome,
      }),
      invalidatesTags: ['incomes'],
    }),
    updateIncome: builder.mutation<any, { id: string; updates: any }>({
      query: ({ id, updates }) => ({
        url: `/accounting/income/${id}`,
        method: 'PATCH',
        body: updates,
      }),
      invalidatesTags: ['incomes'],
    }),
    deleteIncome: builder.mutation<void, string>({
      query: id => ({
        url: `/accounting/income/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['incomes'],
    }),
    approveIncome: builder.mutation<void, string>({
      query: id => ({
        url: `/accounting/income/${id}/approve`,
        method: 'POST',
      }),
      invalidatesTags: ['incomes'],
    }),
    rejectIncome: builder.mutation<void, string>({
      query: id => ({
        url: `/accounting/income/${id}/reject`,
        method: 'POST',
      }),
      invalidatesTags: ['incomes'],
    }),
  }),
});

export const {
  useGetCategoriesQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
  useGetExpensesQuery,
  useCreateExpenseMutation,
  useUpdateExpenseMutation,
  useDeleteExpenseMutation,
  useGetIncomesQuery,
  useCreateIncomeMutation,
  useUpdateIncomeMutation,
  useDeleteIncomeMutation,
  useApproveIncomeMutation,
  useRejectIncomeMutation,
  useApproveExpenseMutation,
  useRejectExpenseMutation,
} = AccountsApi;
